import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Package, Snowflake, Tag, Check, Layers, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { differenceInDays, differenceInHours } from 'date-fns';

interface PlotterCutPackage {
  id: string;
  package_code: string;
  package_name: string;
  materials: Array<{ name: string; type: string; layers: number }>;
  total_layers: number;
  dimensions?: string;
  location?: string;
  nfc_tag_uid?: string;
  cut_date: string;
  status: string;
}

interface FreezerPackageSearchProps {
  onSelect: (pkg: PlotterCutPackage | null) => void;
  selectedPackageId?: string | null;
  customerId?: string;
}

export function FreezerPackageSearch({
  onSelect,
  selectedPackageId,
  customerId,
}: FreezerPackageSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Calcular días en congelador
  const getDaysInFreezer = (cutDate: string) => {
    const days = differenceInDays(new Date(), new Date(cutDate));
    return days;
  };

  // Obtener badge de tiempo en congelador
  const getFreezerTimeBadge = (cutDate: string) => {
    const days = getDaysInFreezer(cutDate);
    if (days > 30) {
      return { color: 'bg-red-500/20 text-red-700 border-red-500/30', label: `${days}d - Revisar`, warn: true };
    }
    if (days > 14) {
      return { color: 'bg-orange-500/20 text-orange-700 border-orange-500/30', label: `${days} días`, warn: false };
    }
    return { color: 'bg-green-500/20 text-green-700 border-green-500/30', label: `${days} días`, warn: false };
  };

  // Handler para seleccionar paquete y registrar timestamp de salida
  const handleSelectPackage = async (pkg: PlotterCutPackage | null) => {
    if (pkg) {
      // Actualizar paquete con timestamp de salida del congelador
      try {
        await api.from('plotter_cuts').update({
          removed_from_freezer_at: new Date().toISOString(),
          removed_from_freezer_by: user?.id,
          status: 'reserved',
        }).eq('id', pkg.id);

        queryClient.invalidateQueries({ queryKey: ['freezer-packages-available'] });
      } catch (error) {
        console.error('Error updating package removal time:', error);
      }
    }
    onSelect(pkg);
  };

  // Fetch available packages in freezer
  const { data: packages, isLoading } = useQuery({
    queryKey: ['freezer-packages-available', customerId],
    queryFn: async () => {
      let query = api
        .from('plotter_cuts')
        .select('id, package_code, package_name, materials, total_layers, dimensions, location, nfc_tag_uid, cut_date, status')
        .eq('status', 'available')
        .eq('is_active', true)
        .is('assigned_work_order_id', null)
        .order('cut_date', { ascending: false });

      // Filter by location containing 'congelador' or 'freezer'
      // Using or() for multiple location patterns
      query = query.or('location.ilike.%congelador%,location.ilike.%freezer%,location.ilike.%frio%');

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as PlotterCutPackage[];
    },
  });

  // Filter by search term
  const filteredPackages = packages?.filter((pkg) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      pkg.package_code?.toLowerCase().includes(term) ||
      pkg.package_name?.toLowerCase().includes(term) ||
      pkg.nfc_tag_uid?.toLowerCase().includes(term)
    );
  });

  const selectedPackage = packages?.find((p) => p.id === selectedPackageId);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-3">
      <Label className="flex items-center gap-2">
        <Snowflake className="w-4 h-4 text-blue-500" />
        Buscar Paquete en Congelador
      </Label>

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por código, nombre o NFC..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Selected Package Display */}
      {selectedPackage && (
        <Card className="border-primary bg-primary/5">
          <CardContent className="p-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium">{selectedPackage.package_code}</p>
                  <p className="text-sm text-muted-foreground">{selectedPackage.package_name}</p>
                  <p className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                    <Snowflake className="w-3 h-3" />
                    {getDaysInFreezer(selectedPackage.cut_date)} días en congelador
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleSelectPackage(null)}
              >
                Cambiar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Package List */}
      {!selectedPackage && (
        <ScrollArea className="h-[200px] rounded-md border">
          {isLoading ? (
            <div className="p-4 text-center text-muted-foreground">
              Buscando paquetes...
            </div>
          ) : filteredPackages?.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No hay paquetes disponibles en el congelador</p>
            </div>
          ) : (
            <div className="p-2 space-y-2">
              {filteredPackages?.map((pkg) => {
                const freezerTime = getFreezerTimeBadge(pkg.cut_date);
                return (
                <Card
                  key={pkg.id}
                  className={cn(
                    'cursor-pointer transition-colors hover:bg-accent',
                    selectedPackageId === pkg.id && 'border-primary bg-primary/5',
                    freezerTime.warn && 'border-orange-500/50'
                  )}
                  onClick={() => handleSelectPackage(pkg)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{pkg.package_code}</span>
                          {pkg.nfc_tag_uid && (
                            <Badge variant="outline" className="text-xs">
                              <Tag className="w-3 h-3 mr-1" />
                              NFC
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{pkg.package_name}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Layers className="w-3 h-3" />
                            {pkg.total_layers} capas
                          </span>
                          {pkg.dimensions && <span>{pkg.dimensions}</span>}
                          <span>Cortado: {formatDate(pkg.cut_date)}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant="secondary" className="text-xs">
                          <Snowflake className="w-3 h-3 mr-1" />
                          {pkg.location || 'Congelador'}
                        </Badge>
                        <Badge className={cn("text-xs", freezerTime.color)}>
                          {freezerTime.warn && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {freezerTime.label}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )})}
            </div>
          )}
        </ScrollArea>
      )}

      <p className="text-xs text-muted-foreground">
        Solo se muestran paquetes con status "disponible" y sin orden asignada
      </p>
    </div>
  );
}
